import { Fragment, useEffect, useState, useCallback } from 'react';
import { RefreshCw, Plus, AlertTriangle, Loader2, FileText, Download, Eye } from 'lucide-react';
import { api } from '../../api/client';

type EppRecord = {
  id: string;
  externalId: number | null;
  parentId: number | null;
  status: string;
  recordNumber: string;
  taxId: string;
  year: number;
  vatPeriodRange: string;
  vatPeriod: string;
  turnoverAsSupplier: any;
  turnoverAsRecipient: any;
  vatForImport: any;
  vatPaidToFarmer: any;
  correctionOfDeductableVat: any;
  recordingDate: string | null;
  createdAt: string;
};

const statusColors: Record<string, { bg: string; color: string }> = {
  Draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  Recorded: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  Corrected: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
};

const monthlyPeriods = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const quarterlyPeriods = ['FirstQuarter', 'SecondQuarter', 'ThirdQuarter', 'FourthQuarter'];

const periodLabels: Record<string, string> = {
  January: 'Januar', February: 'Februar', March: 'Mart', April: 'April',
  May: 'Maj', June: 'Jun', July: 'Jul', August: 'Avgust',
  September: 'Septembar', October: 'Oktobar', November: 'Novembar', December: 'Decembar',
  FirstQuarter: 'Q1', SecondQuarter: 'Q2', ThirdQuarter: 'Q3', FourthQuarter: 'Q4',
};

export function VatDeductionPage({ tenantId }: { tenantId: string }) {
  const [records, setRecords] = useState<EppRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Filters + form
  const thisYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(thisYear);
  const [formYear, setFormYear] = useState(thisYear);
  const [formPeriodRange, setFormPeriodRange] = useState<'Monthly' | 'Quarterly'>('Monthly');
  const [formPeriod, setFormPeriod] = useState<string>(monthlyPeriods[new Date().getMonth()]);
  const [formRecordNumber, setFormRecordNumber] = useState('');
  const [formTaxId, setFormTaxId] = useState('');
  const [prefill, setPrefill] = useState<any | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<EppRecord[]>(`/admin/efaktura-vat/epp?tenantId=${tenantId}&year=${filterYear}`);
      setRecords(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [tenantId, filterYear]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleAutoFill = async () => {
    setBusy('calc');
    setError(null);
    setNotice(null);
    setPrefill(null);
    try {
      const data = await api.post<any>('/admin/efaktura-vat/epp/system-calculation', {
        tenantId,
        year: formYear,
        vatPeriod: formPeriod,
        vatPeriodRange: formPeriodRange,
      });
      setPrefill(data);
      setNotice('Podaci povučeni sa PPPPDV portala. Pregledajte pa kreirajte zapis.');
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri auto-popunjavanju');
    } finally {
      setBusy(null);
    }
  };

  const handleCreate = async () => {
    if (!formRecordNumber.trim() || !formTaxId.trim()) {
      setError('Broj zapisa i PIB su obavezni');
      return;
    }
    setBusy('create');
    setError(null);
    setNotice(null);
    try {
      await api.post('/admin/efaktura-vat/epp', {
        tenantId,
        recordNumber: formRecordNumber,
        taxId: formTaxId,
        year: formYear,
        vatPeriodRange: formPeriodRange,
        vatPeriod: formPeriod,
        turnoverAsSupplier: prefill?.TurnoverAsSupplier,
        turnoverAsRecipient: prefill?.TurnoverAsRecipient,
        vatForImport: prefill?.VatForImport,
        vatPaidToFarmer: prefill?.VatPaidToFarmer,
        correctionOfDeductableVat: prefill?.CorrectionOfDeductableVat,
      });
      setNotice('EPP zapis kreiran.');
      setShowCreate(false);
      setPrefill(null);
      setFormRecordNumber('');
      await fetchRecords();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri kreiranju');
    } finally {
      setBusy(null);
    }
  };

  const handleAnalytics = async () => {
    setBusy('analytics');
    setError(null);
    try {
      const blob = await api.get<any>(`/admin/efaktura-vat/epp/analytics?tenantId=${tenantId}&year=${filterYear}`);
      // Show the data inline or trigger download - we just download JSON for now.
      const json = JSON.stringify(blob, null, 2);
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `epp-analytics-${filterYear}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri preuzimanju analitike');
    } finally {
      setBusy(null);
    }
  };

  const availablePeriods = formPeriodRange === 'Monthly' ? monthlyPeriods : quarterlyPeriods;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Godina:</label>
        <input type="number" value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value) || thisYear)} style={{ ...inputStyle, width: 100 }} />

        <div style={{ flex: 1 }} />
        <button onClick={handleAnalytics} disabled={busy !== null || !tenantId} style={btnSecondary}>
          {busy === 'analytics' ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Analitika
        </button>
        <button onClick={() => setShowCreate((v) => !v)} disabled={!tenantId} style={btnPrimary}>
          <Plus size={14} /> {showCreate ? 'Sakrij' : 'Novi EPP'}
        </button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {showCreate && (
        <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
            Novi EPP zapis
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
            Koristi "Auto-popuni" da povučeš podatke sa PPPPDV portala (system-calculation). Zatim unesi broj zapisa i PIB i klikni "Kreiraj".
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <FormField label="Godina">
              <input type="number" value={formYear} onChange={(e) => setFormYear(parseInt(e.target.value) || thisYear)} style={inputStyle} />
            </FormField>
            <FormField label="Tip perioda">
              <select value={formPeriodRange} onChange={(e) => {
                const val = e.target.value as 'Monthly' | 'Quarterly';
                setFormPeriodRange(val);
                setFormPeriod(val === 'Monthly' ? monthlyPeriods[0] : quarterlyPeriods[0]);
              }} style={selectStyle}>
                <option value="Monthly">Mesečni</option>
                <option value="Quarterly">Kvartalni</option>
              </select>
            </FormField>
            <FormField label="Period">
              <select value={formPeriod} onChange={(e) => setFormPeriod(e.target.value)} style={selectStyle}>
                {availablePeriods.map((p) => <option key={p} value={p}>{periodLabels[p] || p}</option>)}
              </select>
            </FormField>
            <FormField label="Broj zapisa">
              <input type="text" value={formRecordNumber} onChange={(e) => setFormRecordNumber(e.target.value)} style={inputStyle} placeholder="EPP 04/2026" />
            </FormField>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 10 }}>
            <FormField label="PIB">
              <input type="text" value={formTaxId} onChange={(e) => setFormTaxId(e.target.value)} style={inputStyle} placeholder="9 cifara" />
            </FormField>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button onClick={handleAutoFill} disabled={busy !== null} style={btnSecondary}>
                {busy === 'calc' ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Auto-popuni sa PPPPDV
              </button>
              <button onClick={handleCreate} disabled={busy !== null || !formRecordNumber.trim() || !formTaxId.trim()} style={btnPrimary}>
                {busy === 'create' ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Sačuvaj
              </button>
            </div>
          </div>

          {prefill && (
            <div style={{ marginTop: 12, padding: 10, background: 'var(--color-bg)', borderRadius: 6, border: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 6 }}>Pregled prefilled podataka:</div>
              <pre style={{ margin: 0, fontSize: 11, fontFamily: 'Menlo, Consolas, monospace', maxHeight: 240, overflow: 'auto', color: 'var(--color-text)' }}>
                {JSON.stringify(prefill, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Broj zapisa</th>
                <th style={th}>Period</th>
                <th style={th}>PIB</th>
                <th style={th}>Status</th>
                <th style={th}>Parent</th>
                <th style={th}>Datum</th>
                <th style={{ ...th, width: 70 }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                    <FileText size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    <div>Nema EPP zapisa.</div>
                  </td>
                </tr>
              )}
              {records.map((r) => {
                const sc = statusColors[r.status] || statusColors.Draft;
                const expanded = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr>
                      <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>{r.recordNumber}</td>
                      <td style={td}>{r.year} - {periodLabels[r.vatPeriod] || r.vatPeriod}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.taxId}</td>
                      <td style={td}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700 }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ ...td, color: 'var(--color-text-muted)' }}>{r.parentId || '-'}</td>
                      <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {r.recordingDate ? new Date(r.recordingDate).toLocaleDateString('sr-Latn-RS') : '-'}
                      </td>
                      <td style={td}>
                        <button onClick={() => setExpandedId(expanded ? null : r.id)} style={iconBtn} title="Prikaži detalje">
                          <Eye size={12} />
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--color-bg)', padding: 14 }}>
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 6 }}>Ceo zapis (JSON):</div>
                          <pre style={{ margin: 0, fontSize: 11, fontFamily: 'Menlo, Consolas, monospace', maxHeight: 400, overflow: 'auto', color: 'var(--color-text)' }}>
                            {JSON.stringify({
                              TurnoverAsSupplier: r.turnoverAsSupplier,
                              TurnoverAsRecipient: r.turnoverAsRecipient,
                              VatForImport: r.vatForImport,
                              VatPaidToFarmer: r.vatPaidToFarmer,
                              CorrectionOfDeductableVat: r.correctionOfDeductableVat,
                            }, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const inputStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', width: '100%' };
const selectStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', width: '100%' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 4, borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 16 };
